/* A list of parts, condensed.

   Shared by the material page and by every document made of parts, because
   they want exactly the same thing: the headings are the page and the
   explanation opens when it is asked for.

   A part without a title is not folded. That is how an introduction and a
   closing note sit either side of the parts they belong to without any special
   case in the data.

   OWNED BY ITEM 4 OF docs/plan/00-prestavba-2026-08.md, extended by item 2. */

import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Blocks } from "../blocks";
import { Collapse } from "./catalog/Collapse";
import { useI18n } from "../lib/i18n";
import type { Part } from "../lib/types";

export function Folds({ parts, heading, numbered, deckHref }: {
  parts: Part[]; heading?: ReactNode; numbered?: boolean;
  /* When the parts can also be presented, the number is the way in — the
     lecturer who wants slide 19 wants it on the projector, not opened here. */
  deckHref?: (index: number) => string;
}) {
  const { t, ui } = useI18n();
  const [allOpen, setAllOpen] = useState(false);
  if (!parts.length) return null;

  const titled = parts.filter((p) => p.title).length;
  let n = 0;

  return (
    <div className="cat-folds">
      {heading || titled > 1 ? (
        <div className="cat-folds-bar">
          <h2 className="cat-h">{heading ?? ui("material.whole")}</h2>
          {titled > 1 ? (
            <button type="button" className="cat-flat" onClick={() => setAllOpen((v) => !v)}>
              {allOpen ? ui("material.collapseAll") : ui("material.expandAll")}
            </button>
          ) : null}
        </div>
      ) : null}

      {parts.map((part, i) => {
        if (!part.title) {
          return <div key={i} className="fold-loose"><Blocks blocks={part.blocks} /></div>;
        }
        n += 1;
        const at = n;
        const label = (
          <>
            {numbered ? <span className="fold-n">{at}</span> : null}
            {t(part.title)}
            {part.lead ? <span className="fold-lead">{t(part.lead)}</span> : null}
          </>
        );
        return (
          /* The key carries the open state, so "expand all" re-mounts every
             section at the new default instead of leaving the ones already
             touched by hand behind. */
          <Collapse key={`${i}-${allOpen}`} title={label} open={allOpen || (titled === 1)}>
            {part.eyebrow ? <div className="fold-eyebrow">{t(part.eyebrow)}</div> : null}
            {deckHref ? (
              <p className="fold-deck">
                <Link to={deckHref(at)}>{ui("deck.open")} {at} →</Link>
              </p>
            ) : null}
            <Blocks blocks={part.blocks} />
          </Collapse>
        );
      })}
    </div>
  );
}
